import { useEffect, useMemo, useRef, useState } from 'react';
import type { Feed, UIComponent } from '~/services/layout/index.ts';
import { useClientFeedApiEndpoint } from '~/hooks/use-client-feed-api-endpoint';
import { useClientFeed } from '~/hooks/use-client-feed';
import { isEnableDataRefreshing } from '~/services/ui-components';
import { useSubscribeToActionRefreshComponent } from '~/hooks/use-subscribe-to-action-refreshComponent';
import { useLoaderData } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.tsx';

export type ClientFeedWrapperRenderProps = {
  feed: Feed | null | undefined;
  isClient: boolean;
  isLoading: boolean;
};

export type ClientFeedWrapperProps = {
  uiComponent: UIComponent;
  feed?: Feed | null | undefined;
  forceFresh?: boolean;
  render: (props: ClientFeedWrapperRenderProps) => JSX.Element;
};

/**
 * Handles fetching and refreshing feed data on the client while supporting SSR-provided data.
 */
export function ClientFeedWrapper({
  uiComponent,
  feed,
  forceFresh,
  render,
}: ClientFeedWrapperProps): JSX.Element {
  const { isTabsScreen } = useLoaderData<typeof rootLoader>();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchDataRef = useRef<((forceFresh?: boolean) => Promise<void>) | null>(
    null
  );
  const lastFetchedEndpointRef = useRef<string | null>(null);

  const [isClient, setIsClient] = useState<boolean>(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const uiComponentDataSource: string | undefined = uiComponent?.data?.source;

  const clientFeedApiEndpoint: string | null = useClientFeedApiEndpoint({
    uiComponent,
  });

  const { feedState, fetchData } = useClientFeed(clientFeedApiEndpoint, {
    uiComponentId: uiComponent.id,
    initialData: feed ?? null,
  });

  // sync fetchData ref, prevents re-rendering effects when fetchData changes
  fetchDataRef.current = fetchData;

  // sync feedState ref, prevents re-rendering effects when feedState changes
  const feedStateRef = useRef(feedState);
  feedStateRef.current = feedState;

  useSubscribeToActionRefreshComponent({
    clientFeedApiEndpoint,
    fetcherData: feedState.stableData,
    fetchData,
    uiComponent,
  });

  const refreshInterval = uiComponent?.rules?.refreshing_interval;
  const hasRefreshEnabled: boolean =
    isEnableDataRefreshing(uiComponent) && !!refreshInterval;

  // Reset tracking when endpoint changes
  useEffect(() => {
    if (lastFetchedEndpointRef.current !== clientFeedApiEndpoint) {
      lastFetchedEndpointRef.current = null;
    }
  }, [clientFeedApiEndpoint]);

  // Initial fetch - when endpoint changes or when we don't have data
  useEffect(() => {
    if (
      !uiComponentDataSource ||
      !clientFeedApiEndpoint ||
      feedStateRef.current.state !== 'idle' ||
      lastFetchedEndpointRef.current === clientFeedApiEndpoint ||
      (!isTabsScreen &&
        (feedStateRef.current.data || feedStateRef.current.stableData))
    )
      return;

    lastFetchedEndpointRef.current = clientFeedApiEndpoint;
    if (fetchDataRef.current) {
      fetchDataRef.current(!!forceFresh);
    }
  }, [uiComponentDataSource, clientFeedApiEndpoint, forceFresh, isTabsScreen]);

  // Interval for refreshing
  useEffect(() => {
    if (!hasRefreshEnabled || !clientFeedApiEndpoint) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      // Hook's isLoadingRef guard will prevent concurrent requests
      if (fetchDataRef.current) {
        fetchDataRef.current(true);
      }
    }, refreshInterval * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hasRefreshEnabled, clientFeedApiEndpoint, refreshInterval]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const feedToRender = useMemo(
    () => feedState.data || feedState.stableData,
    [feedState.data, feedState.stableData]
  );

  const isLoading = useMemo(() => {
    return hasRefreshEnabled && !feedState.data && feedState.stableData
      ? false
      : hasRefreshEnabled
      ? feedState.state !== 'idle'
      : feedState.state !== 'idle' && !feedState.data;
  }, [
    hasRefreshEnabled,
    feedState.data,
    feedState.stableData,
    feedState.state,
  ]);

  const renderProps = useMemo<ClientFeedWrapperRenderProps>(
    () => ({
      feed: feedToRender,
      isClient,
      isLoading,
    }),
    [feedToRender, isClient, isLoading]
  );

  return render(renderProps);
}
